import { GuillochePattern } from "@shared/components/Brand/GuillochePattern";
import { IndicationUnit } from "@shared/components/Metrics/IndicationUnit";
import { formatCurrency } from "@solomon-apps/shared";
import { Text } from "@solomon-labs/design/components/ui";
import React, { useMemo } from "react";

import { useReportingToken } from "@/hooks/stats/useJachinReporting";
import { useReportingLiveApy } from "@/hooks/stats/useReportingLiveApy";
import { SkeletonReveal } from "@/pages/USDv/components/SkeletonReveal";

function formatSupply(totalSupplyUi: string | undefined): string {
    if (!totalSupplyUi) return "-";
    return formatCurrency(totalSupplyUi, 0, 0);
}

/** Hero headline mode.
 *  - `default` (disconnected or zero-balance wallet) → "Get USDv and earn X% APY"
 *  - `swap-to-usdv` (wallet holds USDC/USDT but no USDv) →
 *    "Your $1,234 could earn X% APY with USDv"
 *  - `usdv-can-earn` (wallet holds USDv but isn't enrolled in YaaS yet) →
 *    "Your USDv can earn X% APY"
 *  Other landing copy (supply in circulation, APY) is unaffected. */
export type HeroSectionMode = "default" | "swap-to-usdv" | "usdv-can-earn";

interface HeroSectionProps {
    mode?: HeroSectionMode;
    /** Sum of the wallet's USDC + USDT balance (raw dollar amount).
     *  Rounded *down* to the nearest dollar in the headline so the
     *  number stays compact and conservative. Used only when
     *  `mode === "swap-to-usdv"`; ignored otherwise. */
    stablesBalance?: number;
}

export function HeroSection({
    mode = "default",
    stablesBalance
}: HeroSectionProps = {}): React.ReactElement {
    // Same source as the Stats page's "Total USDv Supply".
    const { data: tokenReport, isLoading: isSupplyLoading } =
        useReportingToken();
    const { formattedApy, isLoading: isApyLoading } = useReportingLiveApy();

    const supply = useMemo(
        () => formatSupply(tokenReport?.data?.totalSupplyUi),
        [tokenReport?.data?.totalSupplyUi]
    );
    // Stables-balance amount, rounded down to nearest whole dollar,
    // formatted with thousands separators ("$1,234"). Used only in
    // the swap-to-usdv mode; renders inline inside the headline in
    // the same `foreground-base` color as the rest of the leading
    // copy (no tint emphasis).
    const flooredStablesAmount =
        stablesBalance !== null && stablesBalance !== undefined
            ? `$${Math.floor(stablesBalance).toLocaleString("en-US")}`
            : "$0";

    return (
        <div className="relative flex flex-col gap-0 items-center justify-center md:justify-start overflow-hidden md:overflow-visible min-h-[300px] md:min-h-0 w-full md:w-auto border-b border-line-base md:border-b-0">
            {/* Mobile-only guilloche backdrop — wraps the full hero
                content (circulation figure + coin + headline) rather than just the
                coin. The 1283×1283 source SVG has a radial gradient
                (cream → ember → cream) baked in, so the pattern reads
                as a contained vignette without needing an opacity
                multiplier. The outer wrapper sets a fixed mobile
                `min-h-[355px]` so the pattern has the same vertical
                room as the Figma spec (the radial fade then lines up
                with the corners). Clipped by `overflow-hidden`.
                Desktop suppresses via `md:hidden` — the surrounding
                USDvLandingPanel supplies its own panel-wide guilloche. */}
            <div className="md:hidden absolute inset-0 pointer-events-none">
                <GuillochePattern
                    src="/img/guilloche-hero.svg"
                    scale={3.24}
                    animateIn
                />
            </div>

            {/* USDv 3D coin. 96px on mobile, 180px desktop. */}
            <div className="relative size-24 md:size-45 flex items-center justify-center">
                <img
                    src="/img/usdv-coin-3d.png"
                    alt="USDv"
                    className="size-full relative z-10"
                />
            </div>

            {/* Headline — order 3 (bottom) mobile, order 2 (middle) desktop.
                Mobile treatment (try-out, may revert):
                  • `text-4xl` / `tracking-tight-4xl` — twMerge swaps
                    the variant's mobile values, desktop `md:text-7xl`
                    stays.
                  • `max-w-[18rem] text-balance mx-auto` — caps the
                    text column so the headline wraps onto 2 lines
                    with balanced line lengths (text-wrap: balance).
                  • `-mt-10` — pulls the headline up 40px into the
                    coin/guilloche area so the copy overlaps the
                    asset; the asset + pattern read as background
                    behind the text.
                  • `bg-surface-ground relative` — text sits on a
                    paper-colored band that punches a clean band
                    through the guilloche behind it. */}
            <Text
                variant="hero"
                render={<p />}
                className="text-foreground-base text-center md:mt-4 relative z-20 max-w-2xs mx-auto text-balance mt-0 md:max-w-none md:z-auto"
            >
                {mode === "swap-to-usdv"
                    ? `Your ${flooredStablesAmount} could earn `
                    : mode === "usdv-can-earn"
                      ? "Your USDv can earn "
                      : "Get USDv and earn "}
                <span className="text-accent">
                    <SkeletonReveal
                        isLoading={isApyLoading}
                        skeletonWidth="2em"
                        skeletonClassName="h-[0.7em] w-[2em] translate-y-[-0.3em]"
                    >
                        {formattedApy}
                    </SkeletonReveal>{" "}
                    {/* The marker is its own inline box, so without this the
                        line can break between "APY" and its superscript and
                        leave the 1 stranded at the start of the next line. */}
                    <span className="whitespace-nowrap">
                        APY
                        <Text
                            variant="h3"
                            render={
                                <sup className="inline-block align-top pt-2 md:pt-3" />
                            }
                        >
                            1
                        </Text>
                    </span>
                </span>
                {mode === "swap-to-usdv" && " with USDv"}
            </Text>

            {/* Sub-tagline — directly under the headline. `small` on
                mobile, `h3` on desktop: no single variant spans that
                pair, so the two are swapped at `md`. The mobile copy
                mirrors the headline's width cap and `z-20` so it reads
                on the same paper band above the guilloche. */}
            <Text
                variant="small"
                render={<p />}
                className="md:hidden text-foreground-muted text-center relative z-20 max-w-2xs mx-auto text-balance mt-2"
            >
                The only liquid dollar that earns.
            </Text>
            <Text
                variant="h3"
                render={<p />}
                className="hidden md:block text-foreground-muted text-center mt-3"
            >
                The only liquid dollar that earns.
            </Text>

            {/* "$X in circulation" closes the stack, below the
                sub-tagline, at every breakpoint. `pairWith="number"`
                matches the dot to the Text variant beside it, resolving
                size + alignPt per
                breakpoint from the pairing table. `variant="top"` is
                what consumes alignPt, so the row aligns on
                `items-start` and the dot's own top padding seats it
                against the digits' cap height. */}
            <div className="flex gap-2 items-start justify-center mt-4">
                <IndicationUnit variant="top" pairWith="number" />
                <Text
                    variant="number"
                    render={<span />}
                    className="text-foreground-base inline-flex items-center gap-1"
                >
                    <SkeletonReveal
                        isLoading={isSupplyLoading}
                        skeletonWidth="6em"
                        skeletonClassName="h-[1em] w-[5em]"
                    >
                        {supply}
                    </SkeletonReveal>
                    <span>in circulation</span>
                </Text>
            </div>
        </div>
    );
}
